/**
 * Geometria do "ligar com seta arrastando um item sobre o outro" no editor de fluxograma
 * (docs/fluxograma-ligar-arrastando-2026-10-21.md). Funções PURAS, sem React e com import relativo:
 * testadas em backend/tests/fluxograma-ligacao.test.ts.
 */

/** Retângulo de um item em coordenadas absolutas do canvas. */
export interface Retangulo {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** O mínimo de um nó do ReactFlow que a geometria precisa. */
export interface NoGeometria {
  id: string;
  position: { x: number; y: number };
  parentId?: string;
  width?: number | null;
  height?: number | null;
  data?: unknown;
}

type Lado = 'top' | 'right' | 'bottom' | 'left';

const dimensao = (n: NoGeometria) => {
  const d = (n.data ?? {}) as { width?: number; height?: number };
  return {
    w: typeof n.width === 'number' ? n.width : d.width ?? 0,
    h: typeof n.height === 'number' ? n.height : d.height ?? 0,
  };
};

/** Retângulos absolutos (somando a posição do pai, um nível: raias não ficam dentro de raias). */
export function retangulosAbsolutos(nos: NoGeometria[]): Retangulo[] {
  const porId = new Map(nos.map((n) => [n.id, n] as const));
  return nos.map((n) => {
    const pai = n.parentId ? porId.get(n.parentId) : undefined;
    const { w, h } = dimensao(n);
    return { id: n.id, x: (pai?.position.x ?? 0) + n.position.x, y: (pai?.position.y ?? 0) + n.position.y, w, h };
  });
}

/** Centro do item arrastado em coordenadas absolutas (posição relativa ao pai + posição do pai). */
export function centroAbsoluto(no: NoGeometria, posicaoPai: { x: number; y: number } | null): { x: number; y: number } {
  const { w, h } = dimensao(no);
  return { x: (posicaoPai?.x ?? 0) + no.position.x + w / 2, y: (posicaoPai?.y ?? 0) + no.position.y + h / 2 };
}

/**
 * Alvo sob o ponto: o ÚLTIMO candidato (na ordem de desenho) que contém o ponto, ou seja, o que está por cima.
 * `candidatos` já vem sem o próprio arrastado e sem containers/raias.
 */
export function alvoNoPonto(candidatos: Retangulo[], ponto: { x: number; y: number }): string | null {
  for (let i = candidatos.length - 1; i >= 0; i--) {
    const r = candidatos[i];
    if (ponto.x >= r.x && ponto.x <= r.x + r.w && ponto.y >= r.y && ponto.y <= r.y + r.h) return r.id;
  }
  return null;
}

/**
 * Lados da seta "item de baixo → arrastado", pela posição do arrastado (já de volta ao lugar original) em
 * relação ao item de baixo: um acima do outro liga pelo topo/base; lado a lado, pela esquerda/direita.
 * Devolve os ids de handle do ShapeNode: saída = lado, entrada = `t-<lado>`.
 */
export function ladosDaSeta(
  origem: { x: number; y: number },
  destino: { x: number; y: number },
): { sourceHandle: Lado; targetHandle: `t-${Lado}` } {
  const dx = destino.x - origem.x;
  const dy = destino.y - origem.y;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy >= 0 ? { sourceHandle: 'bottom', targetHandle: 't-top' } : { sourceHandle: 'top', targetHandle: 't-bottom' };
  }
  return dx >= 0 ? { sourceHandle: 'right', targetHandle: 't-left' } : { sourceHandle: 'left', targetHandle: 't-right' };
}
